/**
 * Cabine miroirs: a mirror maze drawn behind the first photo of the product media gallery.
 *
 * Progressive enhancement over Horizon's `<media-gallery>`: the gallery keeps its markup, images and
 * navigation. This element, rendered hidden by blocks/cabine-miroirs.liquid, places a canvas behind the
 * first visible media, draws the scene there, and removes it as soon as it could get in the way of the
 * purchase (slow loading, slow device, lost graphics context). No dependency on the theme's modules: it
 * reads the gallery DOM and listens to its public events.
 *
 * Rendering is ported from the approved mock-up (docs/cabine-miroirs-maquette.html): three full-frame
 * passes without geometry. Analytic ray tracing of the maze, a blur weighted by a reflection mask, then
 * the composition (soft reflections, vignette, grain, fade to the background).
 */

/* ---------------------------------------------------------------------------------------------------
 * Scene constants: the look of the home page maze (light theme). Lengths in metres, colours in sRGB.
 * ------------------------------------------------------------------------------------------------- */

const HOME = {
  zenith: '#ffffff', // sky at the zenith
  horizon: '#46525a', // sky at the horizon line
  skyIntensity: 3.2,
  skyPower: 8, // sharpness of the horizon → zenith gradient
  lightIntensity: 0.6, // key light on the floor
  floorColor: '#f4f7f9',
  floorSpecular: 0.2, // satin floor: share of the reflection
  seam: 0.55, // darkness of the joints between panels
  capTint: '#d6dde1', // chrome: head tubes, skirting tubes, posts
  wallHeight: 3.2,
  capRadius: 0.042, // head tube
  railRadius: 0.05, // skirting tube
  postRadius: 0.06,
  panelWidth: 1.2, // straight walls (V, hexagon): a joint every 1.20 m
  waveStep: 0.001, // sheet waviness for each step of the "Ondulation de la tôle" setting
  exposure: 1.45,
  contrast: 1.18,
  saturation: 0.8,
  lift: '#101820',
  gain: '#f6faff',
  vignette: 0.08,
};

/** Height of the product centre above the floor. */
const PRODUCT_Y = 1.35;

/**
 * Product height the camera framings are designed around. The virtual product itself takes the size
 * and place of the photo, measured in the page: the framing stays the mock-up's whatever the margins
 * and format of the gallery.
 */
const REFERENCE_HEIGHT = 0.75;

/** Camera framings. `share`: part of the frame height taken by a REFERENCE_HEIGHT product. */
const CAMERAS = {
  // Desktop: standing in the corridor, facing the entrance of the room (as on the home page).
  wide: { fov: 30, share: 0.37, height: 0.1 },
  // Mobile V: closer and wider, so both panels show the back from three-quarters.
  tall: { fov: 38, share: 0.31, height: 0.14 },
};

const DEG = Math.PI / 180;

/**
 * Hexagon of 1.20 m panels, open on the side facing the camera (+z): 6 corners, 5 walls.
 * Angles are measured from +z, like `atan(x, z)` in the shader.
 */
const HEX_CORNERS = [30, 90, 150, 210, 270, 330].map((a) => [
  Math.sin(a * DEG) * HOME.panelWidth,
  Math.cos(a * DEG) * HOME.panelWidth,
]);

/** Mobile V: two 2.60 m panels turned by 20°, tip 1.60 m behind the product. */
const V_TIP = [0, -1.6];
const V_END = [Math.cos(20 * DEG) * 2.6, -1.6 + Math.sin(20 * DEG) * 2.6];

/**
 * Shapes. Rings: `a` apothem, `n` panels, `phase` in panels, `open` passages as [centre°, half-width°].
 * Walls: straight panels [a, b] in the floor plane (x, z). Corners: posts at wall ends, with the
 * walls they join. `physical`: the product is a plane at its real place (otherwise a billboard facing
 * each ray, showing the front or the back depending on the angle). `wallWave`: walls rise in a wave
 * from the back, like the ring panels (otherwise all together).
 */
const SHAPES = {
  round: {
    camera: 'wide',
    physical: false,
    wallWave: false,
    rings: [
      // Opening of three panels (JB, 27/09): the two on either side of the entrance, the closest to the
      // camera, came in at the edges of the frame as soon as the mouse turned the view.
      { a: 2.2, n: 16, phase: 0, open: [[0, 33.75]] },
      {
        a: 4.6,
        n: 24,
        phase: 0.5,
        open: [
          [100, 7.5],
          [-100, 7.5],
        ],
      },
      { a: 6.2, n: 32, phase: 0.5, open: [] },
    ],
    walls: [],
    corners: [],
  },
  hex: {
    camera: 'wide',
    physical: false,
    wallWave: true,
    rings: [],
    walls: [0, 1, 2, 3, 4].map((i) => [HEX_CORNERS[i], HEX_CORNERS[i + 1]]),
    corners: HEX_CORNERS.map((point, i) => ({ point, walls: [i - 1, i].filter((w) => w >= 0 && w < 5) })),
  },
  v: {
    camera: 'tall',
    physical: true,
    wallWave: false,
    rings: [
      {
        a: 3.9,
        n: 20,
        phase: 0.5,
        open: [
          [108, 9],
          [-108, 9],
        ],
      },
      { a: 5.5, n: 28, phase: 0.5, open: [] },
    ],
    walls: [
      [V_TIP, V_END],
      [[-V_END[0], V_END[1]], V_TIP],
    ],
    corners: [
      { point: [-V_END[0], V_END[1]], walls: [1] },
      { point: V_TIP, walls: [0, 1] },
      { point: V_END, walls: [0] },
    ],
  },
};

/** Quality tiers: pixel density cap, render scale, bounce cap, blur. Past the last one, the cabin stops. */
const TIERS = [
  { name: 'haute', dpr: 1.75, scale: 1, bounces: 4, blur: true },
  { name: 'moyenne', dpr: 1.4, scale: 0.8, bounces: 3, blur: true },
  { name: 'basse', dpr: 1.1, scale: 0.7, bounces: 2, blur: false },
];

/**
 * Budgets. `initMs`: programs and textures ready after this long from the start, or stop.
 * `frameMs`: benchmark frame budget of each tier. `runMs` × `runFrames`: that many slow frames in a row
 * while animating drop one tier.
 */
const BUDGET = { initMs: 900, frameMs: [11, 19, 30], runMs: 42, runFrames: 10 };

/** Motion timings, in seconds for an appearance of APPEAR_REFERENCE seconds (the mock-up's). */
const APPEAR_REFERENCE = 1.1;
const APPEAR_END = 2;
/** Keep the loop alive this long after the last scroll or pointer event, to follow the gesture. */
const KEEP_ALIVE_MS = 300;
/**
 * At rest, the image is redrawn at the screen's own pixel density (up to REFINE_DPR), averaging
 * REFINE_SAMPLES rays per pixel, one per frame, each shifted inside its pixel: sharp chrome tubes and panel
 * joints, where a gesture's image (one ray per pixel, reduced density) shows stairs.
 */
/**
 * Trial (JB, 27/09): the highest quality all the time, whatever it costs. A gesture's image at the screen's
 * density with GESTURE_SAMPLES rays per pixel, 16 at rest, no benchmark and no dropping a tier.
 */
const MAX_QUALITY = false;
const GESTURE_SAMPLES = 4;
const REFINE_SAMPLES = MAX_QUALITY ? 16 : 8;
const REFINE_DPR = 2;

/** Parallax: turn of the view around the product, in radians, at the edge of the photo (mouse). */
const POINTER = { yaw: 0.08, pitch: 0.04 };

/**
 * Tilt of the phone (mobile): turn of the view at full tilt, tilt that gives it (degrees), time the rest
 * position takes to follow the way the phone is held (seconds), and changes too small to redraw for
 * (degrees, hand tremor).
 */
const TILT = { yaw: 0.14, pitch: 0.06, range: 25, recenter: 4, deadZone: 0.3 };
/** Fade of the canvas when the cabin stops. */
const FADE_MS = 500;
/** Longest wait for the browser to be idle after `load`. */
const IDLE_TIMEOUT_MS = 1500;

/** Desktop layout of Horizon's gallery, and of the SDM product sheet (assets/sdm-fiche.css). */
const DESKTOP_QUERY = '(min-width: 750px)';
const SDM_DESKTOP_QUERY = '(min-width: 721px)';

/** Galleries the cabin can sit in: Horizon's, and the SDM product sheet's (sections/sdm-fiche.liquid). */
const GALLERY_SELECTOR = 'media-gallery, .sdm-fiche-media';
const SESSION_KEY = 'mirror-cabin:off';
const LOG_PREFIX = '[cabine miroirs]';

/** Theme setting defaults (config/settings_schema.json), used for any value missing from the block. */
const DEFAULTS = {
  missingBack: 'hide',
  shapeDesktop: 'round',
  shapeMobile: 'v',
  backgroundSource: 'section',
  background: '#EEF3F6',
  depth: 4,
  blur: 55,
  grain: 1,
  grainReflections: 0,
  wave: 1,
  tint: '#D2D9DD',
  appear: 'rise',
  appearDuration: 1.1,
  hideOnNext: true,
  parallax: true,
  gyro: true,
  qualityMobile: 'standard',
};

/* ---------------------------------------------------------------------------------------------------
 * Shaders
 * ------------------------------------------------------------------------------------------------- */

const MAXR = 3;
const MAXW = 6;
const MAXP = 10;

/** GLSL float literal. */
const f = (n) => (Number.isInteger(n) ? `${n}.` : `${n}`);

const VS = `#version 300 es
out vec2 vUv; out vec2 vNdc;
void main(){ vec2 p = vec2(float((gl_VertexID << 1) & 2), float(gl_VertexID & 2)); vUv = p; vNdc = p * 2. - 1.; gl_Position = vec4(p * 2. - 1., 0., 1.); }`;

/**
 * The scene: analytic ray tracing (panel rings, straight walls, posts, floor) with the home page's
 * material, light and grading. Output: graded colour, and in alpha a reflection mask (0 for what is
 * seen directly, such as chrome and the near floor, up to 1 for distant reflections, the softest).
 */
const SCENE_FS = `#version 300 es
precision highp float;
#define PI 3.141592653589793
in vec2 vNdc; out vec4 frag;
uniform vec3 uCam; uniform mat4 uInvVP; uniform vec2 uJit;
uniform int uNR, uNW, uNP;
uniform float uRA[${MAXR}], uRN[${MAXR}], uRPh[${MAXR}], uRG[${MAXR}]; uniform vec4 uROp[${MAXR}];
uniform vec2 uWA[${MAXW}], uWB[${MAXW}]; uniform float uWH[${MAXW}]; uniform vec3 uPost[${MAXP}];
uniform float uWallH, uHAvg, uMaxB;
uniform vec3 uZenith, uHorizon, uFloor, uWallTint, uCapTint, uLift, uGain;
uniform float uSkyPow, uFloorSpec, uLightI, uSeam, uWave, uCapR, uRailR, uPostR;
uniform float uExposure, uContrast, uSaturation;
uniform sampler2D uFront, uBack; uniform vec2 uCard; uniform float uProdX, uProdY, uPhys, uPx;

float hash11(float p) { p = fract(p * .1031); p *= p + 33.33; p *= p + p; return fract(p); }
float angD(float a, float b) { return abs(mod(a - b + PI, 2. * PI) - PI); }
vec3 skyColor(vec3 rd) { return mix(uHorizon, uZenith, pow(clamp(rd.y, 0., 1.), uSkyPow)); }
vec3 skyAmbient() { return mix(uHorizon, uZenith, 2. / (uSkyPow + 2.)); }
vec3 envColor(vec3 rd) {
  vec3 wall = uFloor * (skyAmbient() * .9 + vec3(uLightI) * .75);
  vec3 c = mix(wall, skyColor(rd), smoothstep(.3, .44, rd.y));
  return mix(c, uHorizon, smoothstep(.028, 0., abs(rd.y - .3)) * .85);
}
vec3 fresnelMetal(vec3 f0, float c) { return f0 + (1. - f0) * pow(1. - clamp(c, 0., 1.), 5.); }
float wave(float u, float y, float seed) {
  float id = floor(u), fu = fract(u);
  float r1 = hash11(id * 1.37 + seed), r2 = hash11(id * 2.71 + seed + 5.3), yy = y / 2.8;
  return sin(fu * PI) * (.65 * sin(fu * PI * (1. + floor(r1 * 2.)) + yy * PI * (.5 + r2) + r1 * 6.2832)
                       + .35 * sin(fu * 2. * PI * (1. + r2) - yy * PI * 1.5 + r2 * 6.2832));
}
bool inOpening(int i, float phi) { vec4 o = uROp[i]; return (o.y > 0. && angD(phi, o.x) < o.y) || (o.w > 0. && angD(phi, o.z) < o.w); }
float facetH(int i, float k) {
  float th = uRPh[i] + k * 2. * PI / uRN[i];
  float x = clamp(uRG[i] * 1.35 - angD(th, PI) / PI * .35, 0., 1.);
  return uWallH * x * x * (3. - 2. * x);
}
float wallDist(vec2 p) {
  float d = 1e9;
  for (int i = 0; i < ${MAXR}; i++) { if (i >= uNR) break; d = min(d, abs(length(p) - uRA[i])); }
  for (int i = 0; i < ${MAXW}; i++) { if (i >= uNW) break; vec2 a = uWA[i], ba = uWB[i] - a, pa = p - a; d = min(d, length(pa - ba * clamp(dot(pa, ba) / dot(ba, ba), 0., 1.))); }
  return d;
}
vec3 floorRadiance(vec3 p) {
  float ao = mix(1., mix(.5, 1., smoothstep(0., .8, wallDist(p.xz))), uHAvg);
  vec2 q = (p.xz - vec2(uProdX, 0.)) / vec2(uCard.x * .75, uCard.x * .42);
  float sh = 1. - .45 * (1. - clamp((uProdY - ${f(PRODUCT_Y)}) / 1.2, 0., 1.)) * exp(-dot(q, q));
  return uFloor * (skyAmbient() * ao + vec3(uLightI) * sh * mix(1., ao, .6));
}
vec3 fallbackEnv(vec3 rd) {
  vec3 fl = uFloor * (skyAmbient() * .7 + vec3(uLightI) * .6);
  return mix(fl, envColor(rd), smoothstep(-.3, .5, rd.y)) * uWallTint * .6;
}
vec4 productHit(vec3 O, vec3 D, float nb, float travel, out float tP) {
  tP = 1e9;
  vec2 st; float t; bool fromFront;
  if (uPhys > .5) {
    if (abs(D.z) < 1e-5) return vec4(0.);
    t = -O.z / D.z;
    if (t <= 1e-4) return vec4(0.);
    vec3 H = O + D * t;
    st = vec2((H.x - uProdX) / uCard.x + .5, (H.y - uProdY) / uCard.y + .5);
    fromFront = D.z < 0.;
    bool odd = mod(nb, 2.) > .5;
    st.x = fromFront ? (odd ? 1. - st.x : st.x) : (odd ? st.x : 1. - st.x);
  } else {
    float l = length(D.xz);
    if (l < 1e-4) return vec4(0.);
    vec2 dh = D.xz / l, o = O.xz - vec2(uProdX, 0.);
    t = -dot(o, dh) / l;
    if (t <= 1e-4) return vec4(0.);
    vec3 H = O + D * t;
    st = vec2(dot(H.xz - vec2(uProdX, 0.), vec2(-dh.y, dh.x)) / uCard.x + .5, (H.y - uProdY) / uCard.y + .5);
    if (mod(nb, 2.) > .5) st.x = 1. - st.x;
    fromFront = dh.y < 0.;
  }
  if (st.x <= 0. || st.x >= 1. || st.y <= 0. || st.y >= 1.) return vec4(0.);
  float texW = float(fromFront ? textureSize(uFront, 0).x : textureSize(uBack, 0).x);
  float lod = max(log2(uPx * (travel + t) * texW / uCard.x) - .5, 0.);
  tP = t;
  return fromFront ? textureLod(uFront, st, lod) : textureLod(uBack, st, lod);
}

void main() {
  vec4 fp = uInvVP * vec4(vNdc + uJit, 1., 1.);
  vec3 rd = normalize(fp.xyz / fp.w - uCam), ro = uCam;
  vec3 col = vec3(0.), thr = vec3(1.);
  float nb = 0., travel = 0., firstKind = -1., reflD = 0.;
  bool skipP = true, done = false;
  float A = 0.;
  for (int it = 0; it < 12; it++) {
    A = dot(rd.xz, rd.xz);
    float tB = 1e9, sP = 0., yP = 0., wid = 0., pw = ${f(HOME.panelWidth)};
    int kind = -1;
    vec3 nrm = vec3(0.);
    if (rd.y < -1e-5) { float t = -ro.y / rd.y; if (t > 1e-4) { tB = t; kind = 0; nrm = vec3(0., 1., 0.); } }
    if (A > 1e-8) {
      for (int i = 0; i < ${MAXR}; i++) {
        if (i >= uNR) break;
        float a = uRA[i], dA = 2. * PI / uRN[i], r = a * (1. + 1. / cos(dA * .5)) * .5;
        float B = dot(ro.xz, rd.xz), Cc = dot(ro.xz, ro.xz) - r * r, disc = B * B - A * Cc;
        if (disc < 0.) continue;
        float sq = sqrt(disc);
        for (int q = 0; q < 2; q++) {
          float t = (-B + (q == 0 ? -sq : sq)) / A;
          if (t <= 1e-4 || t >= tB) continue;
          vec3 p = ro + rd * t;
          float phi = atan(p.x, p.z);
          if (inOpening(i, phi)) continue;
          float fs = (phi - uRPh[i]) / dA + .5, kf = floor(fs), Hh = facetH(i, kf);
          if (Hh < .005 || p.y < 0. || p.y > Hh + uCapR) continue;
          float th = uRPh[i] + kf * dA;
          vec3 u = vec3(sin(th), 0., cos(th)), n = u * -sign(dot(rd, u));
          tB = t; yP = p.y; wid = float(i) * 101. + kf; pw = dA * a; sP = fract(fs) * pw;
          if (p.y > Hh - uCapR) { kind = 2; float s = clamp((p.y - Hh) / uCapR, -1., 1.); nrm = normalize(n * sqrt(max(1. - s * s, 0.)) + vec3(0., s, 0.)); }
          else if (p.y < uRailR) { kind = 2; float s = p.y / uRailR; nrm = normalize(n * sqrt(max(1. - s * s, 0.)) + vec3(0., s, 0.)); }
          else { kind = 1; nrm = n; }
          break;
        }
      }
    }
    for (int i = 0; i < ${MAXW}; i++) {
      if (i >= uNW) break;
      float Hh = uWH[i];
      if (Hh < .005) continue;
      vec2 a = uWA[i], b = uWB[i];
      float Lw = length(b - a);
      vec2 d = (b - a) / Lw, n2 = vec2(-d.y, d.x);
      float dn = dot(rd.xz, n2);
      if (abs(dn) > 1e-6) {
        float t = dot(a - ro.xz, n2) / dn;
        if (t > 1e-4 && t < tB) {
          vec3 p = ro + rd * t;
          float u = dot(p.xz - a, d);
          if (u >= 0. && u <= Lw && p.y >= 0. && p.y <= Hh + uCapR) {
            vec3 n = vec3(n2.x, 0., n2.y) * -sign(dn);
            tB = t; yP = p.y; wid = 300. + float(i); pw = ${f(HOME.panelWidth)}; sP = u;
            if (p.y > Hh - uCapR) { kind = 2; float s = clamp((p.y - Hh) / uCapR, -1., 1.); nrm = normalize(n * sqrt(max(1. - s * s, 0.)) + vec3(0., s, 0.)); }
            else if (p.y < uRailR) { kind = 2; float s = p.y / uRailR; nrm = normalize(n * sqrt(max(1. - s * s, 0.)) + vec3(0., s, 0.)); }
            else { kind = 1; nrm = n; }
          }
        }
      }
    }
    for (int j = 0; j < ${MAXP}; j++) {
      if (j >= uNP) break;
      vec3 pp = uPost[j];
      if (pp.z < .005 || A < 1e-8) continue;
      vec2 oc = ro.xz - pp.xy;
      float B = dot(oc, rd.xz), Cc = dot(oc, oc) - uPostR * uPostR, disc = B * B - A * Cc;
      if (disc < 0.) continue;
      float t = (-B - sqrt(disc)) / A;
      if (t > 1e-4 && t < tB) { float y = ro.y + rd.y * t; if (y >= 0. && y <= pp.z + uCapR) { tB = t; kind = 2; vec2 cn = normalize(oc + rd.xz * t); nrm = vec3(cn.x, 0., cn.y); } }
    }
    if (!skipP) {
      float tP; vec4 pc = productHit(ro, rd, nb, travel, tP);
      if (tP < tB && pc.a > .002) {
        col += thr * pc.a * pc.rgb * .9;
        thr *= 1. - pc.a; ro += rd * tP; travel += tP; reflD += tP; skipP = true;
        continue;
      }
    }
    if (firstKind < -.5) firstKind = float(kind);
    else reflD += min(tB, 30.);
    if (kind < 0) { col += thr * envColor(rd); done = true; break; }
    vec3 p = ro + rd * tB;
    travel += tB;
    if (kind == 0) {
      float F = .04 + .96 * pow(1. - max(-rd.y, 0.), 5.);
      float k = clamp(uFloorSpec * mix(.3, 1., F), 0., 1.);
      col += thr * (1. - k) * floorRadiance(p);
      thr *= k;
      if (nb >= uMaxB) { col += thr * fallbackEnv(reflect(rd, vec3(0., 1., 0.))); done = true; break; }
      rd = reflect(rd, vec3(0., 1., 0.)); ro = p + vec3(0., .002, 0.); nb += 1.; skipP = false;
      continue;
    }
    if (nb >= uMaxB) { col += thr * fallbackEnv(rd); done = true; break; }
    vec3 n = nrm, tint = uCapTint;
    float shade = 1.;
    if (kind == 1) {
      // mirror sheet: very light waviness, joints between panels (dark slit and a chamfer catching the light)
      vec3 T = normalize(vec3(-n.z, 0., n.x));
      const float e = .01;
      float seed = wid * 7.31;
      float dws = (wave((sP + e) / pw, yP, seed) - wave((sP - e) / pw, yP, seed)) / (2. * e);
      float dwy = (wave(sP / pw, yP + e, seed) - wave(sP / pw, yP - e, seed)) / (2. * e);
      n = normalize(n - (T * dws + vec3(0., .5, 0.) * dwy) * uWave);
      float fu = fract(sP / pw + .5) - .5, ds = abs(fu) * pw, fw = max(uPx * travel, 1e-4);
      float gap = (1. - smoothstep(.0015, .0015 + fw, ds)) * min(1., .006 / max(fw, .002));
      n = normalize(n + T * sign(fu) * (1. - smoothstep(0., .01 + fw, ds)) * .4);
      shade = 1. - uSeam * gap;
      tint = uWallTint;
    } else shade = mix(.3, 1., smoothstep(-.3, .75, n.y));
    thr *= fresnelMetal(tint, dot(-rd, n)) * shade;
    rd = reflect(rd, n); ro = p + n * .004; nb += 1.; skipP = false;
  }
  if (!done) col += thr * fallbackEnv(rd);

  // home grading (ACES, saturation, contrast, lift / gain); vignette and grain come with the composition
  vec3 x = col * uExposure;
  vec3 c = clamp((x * (2.51 * x + .03)) / (x * (2.43 * x + .59) + .14), 0., 1.);
  c = mix(c * 12.92, 1.055 * pow(c, vec3(1. / 2.4)) - .055, step(.0031308, c));
  c = mix(vec3(dot(c, vec3(.2126, .7152, .0722))), c, uSaturation);
  c = (c - .5) * uContrast + .5;
  c = uLift + c * (uGain - uLift);
  // reflection mask: 0 for what is seen directly (chrome, near floor), up to 1 for distant reflections
  float w = firstKind > .5 && firstKind < 1.5 ? smoothstep(.1, 3.2, reflD)
          : firstKind < .5 && firstKind > -.5 ? .75 * smoothstep(.05, 2.2, reflD)
          : firstKind > 1.5 ? .3 * smoothstep(.1, 2., reflD) : 0.;
  frag = vec4(clamp(c, 0., 1.), w);
}`;

/** Separable blur weighted by the mask: reflections soften without bleeding on chrome or the product. */
const BLUR_FS = `#version 300 es
precision highp float;
in vec2 vUv; out vec4 frag;
uniform sampler2D uSrc; uniform vec2 uDir;
void main(){
  float k[5] = float[5](.2270270, .1945946, .1216216, .0540541, .0162162);
  vec4 acc = vec4(0.);
  for (int i = -4; i <= 4; i++) {
    vec4 c = texture(uSrc, vUv + uDir * float(i));
    float w = k[abs(i)] * c.a;
    acc += vec4(c.rgb * w, w);
  }
  frag = vec4(acc.rgb / max(acc.a, 1e-4), acc.a);
}`;

/** Composition: softened reflections, home vignette, image grain and reflection grain, fade to background. */
const COMP_FS = `#version 300 es
precision highp float;
in vec2 vUv; in vec2 vNdc; out vec4 frag;
uniform sampler2D uSharp, uSoft; uniform float uBlur, uGrain, uGrainR, uVignette, uAspect, uRoom, uUseSoft;
uniform vec3 uBgS; uniform float uFxaa; uniform vec2 uTexel;
float hash12(vec2 p) { vec3 p3 = fract(vec3(p.xyx) * .1031); p3 += dot(p3, p3.yzx + 33.33); return fract((p3.x + p3.y) * p3.z); }
// A gesture's image (one ray per pixel): FXAA, edges smoothed along their direction, in the scene's pixels.
vec3 fxaa(vec2 uv, vec3 rgbM) {
  const vec3 L = vec3(.299, .587, .114);
  vec3 nw = texture(uSharp, uv + vec2(-1., -1.) * uTexel).rgb, ne = texture(uSharp, uv + vec2(1., -1.) * uTexel).rgb;
  vec3 sw = texture(uSharp, uv + vec2(-1., 1.) * uTexel).rgb, se = texture(uSharp, uv + vec2(1., 1.) * uTexel).rgb;
  float lNW = dot(nw, L), lNE = dot(ne, L), lSW = dot(sw, L), lSE = dot(se, L), lM = dot(rgbM, L);
  float lMin = min(lM, min(min(lNW, lNE), min(lSW, lSE))), lMax = max(lM, max(max(lNW, lNE), max(lSW, lSE)));
  vec2 dir = vec2(-((lNW + lNE) - (lSW + lSE)), (lNW + lSW) - (lNE + lSE));
  float reduce = max((lNW + lNE + lSW + lSE) * (.25 / 8.), 1. / 128.);
  dir = clamp(dir / (min(abs(dir.x), abs(dir.y)) + reduce), vec2(-8.), vec2(8.)) * uTexel;
  vec3 a = .5 * (texture(uSharp, uv + dir * (1. / 3. - .5)).rgb + texture(uSharp, uv + dir * (2. / 3. - .5)).rgb);
  vec3 b = a * .5 + .25 * (texture(uSharp, uv - dir * .5).rgb + texture(uSharp, uv + dir * .5).rgb);
  float lB = dot(b, L);
  return lB < lMin || lB > lMax ? a : b;
}
void main(){
  vec4 s = texture(uSharp, vUv);
  vec3 c = uFxaa > .5 ? fxaa(vUv, s.rgb) : s.rgb;
  if (uUseSoft > .5) { vec4 b = texture(uSoft, vUv); c = mix(c, b.rgb, clamp(s.a * uBlur, 0., 1.) * step(1e-3, b.a)); }
  vec2 q = vNdc * .5; q.x *= uAspect;
  c *= 1. - uVignette * smoothstep(.2, .95, length(q) * 1.15);
  c += (hash12(gl_FragCoord.xy) - .5) * (uGrain + uGrainR * s.a);
  frag = vec4(clamp(mix(uBgS, c, uRoom), 0., 1.), 1.);
}`;

/* ---------------------------------------------------------------------------------------------------
 * Helpers
 * ------------------------------------------------------------------------------------------------- */

const clamp01 = (x) => Math.min(Math.max(x, 0), 1);
const smooth = (a, b, x) => {
  const t = clamp01((x - a) / (b - a));
  return t * t * (3 - 2 * t);
};
const easeOut = (x) => 1 - Math.pow(1 - x, 3);
const angleDistance = (a, b) =>
  Math.abs(((((a - b + Math.PI) % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI)) - Math.PI);
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const toLinear = (rgb) => rgb.map((v) => (v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)));

/** @type {CanvasRenderingContext2D | null | undefined} */
let colorContext;

/**
 * Any CSS colour → sRGB components in 0–1 with alpha, or null if the value isn't a colour.
 * @param {string | null | undefined} value
 * @returns {number[] | null}
 */
function parseColor(value) {
  if (!value) return null;
  if (colorContext === undefined) {
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = 1;
    colorContext = canvas.getContext('2d', { willReadFrequently: true });
  }
  const ctx = colorContext;
  if (!ctx) return null;
  // An invalid colour leaves fillStyle unchanged: detect it with two different sentinels.
  ctx.fillStyle = '#000000';
  ctx.fillStyle = value;
  const first = ctx.fillStyle;
  ctx.fillStyle = '#ffffff';
  ctx.fillStyle = value;
  if (ctx.fillStyle !== first) return null;
  ctx.clearRect(0, 0, 1, 1);
  ctx.fillRect(0, 0, 1, 1);
  const [r, g, b, a] = ctx.getImageData(0, 0, 1, 1).data;
  return a ? [r / 255, g / 255, b / 255, a / 255] : [0, 0, 0, 0];
}

/** @param {string} hex */
const srgb = (hex) => /** @type {number[]} */ (parseColor(hex)).slice(0, 3);
/** @param {string} hex */
const linear = (hex) => toLinear(srgb(hex));
/** @param {number[]} rgb @param {number} k */
const scaled = (rgb, k) => rgb.map((v) => v * k);

function isDesignMode() {
  return Boolean(/** @type {any} */ (window).Shopify?.designMode);
}

function sessionGet() {
  try {
    return sessionStorage.getItem(SESSION_KEY);
  } catch {
    return null;
  }
}

/** @param {string} reason */
function sessionSet(reason) {
  try {
    sessionStorage.setItem(SESSION_KEY, reason);
  } catch {
    // Storage blocked (private mode, policies): the cabin may try again on the next page.
  }
}

/**
 * A failure that stops the cabin, with the reason logged to the console. Failures tied to the device
 * (WebGL, frame budgets, lost context) are remembered for the session; the others only stop this page.
 * `timing`: a budget failure, meaningless if the tab went to the background meanwhile.
 */
class CabinStop extends Error {
  /** @param {string} reason @param {{ remember?: boolean, timing?: boolean }} [options] */
  constructor(reason, { remember = true, timing = false } = {}) {
    super(reason);
    this.remember = remember;
    this.timing = timing;
  }
}

/**
 * The first start of a browser compiles the shaders with an empty GPU cache and downloads the photos: a
 * slow first load says little about the device, so it only stops the page, and the next one tries again.
 */
const slowLoad = () => new CabinStop('chargement trop long', { remember: false, timing: true });

/** The start can't finish now (photo moved or hidden meanwhile): give up quietly and try again later. */
class CabinRetry extends Error {}

/* ---------------------------------------------------------------------------------------------------
 * Renderer: WebGL2 programs, textures, render targets and the three passes.
 * ------------------------------------------------------------------------------------------------- */

class Renderer {
  /** @type {WebGL2RenderingContext | null} */
  gl = null;
  /** @type {Record<string, WebGLProgram>} */
  programs = {};
  /** @type {WeakMap<WebGLProgram, Map<string, WebGLUniformLocation | null>>} */
  #locations = new WeakMap();
  /** @type {{ tex: WebGLTexture, fb: WebGLFramebuffer, w: number, h: number }[]} */
  #targets = [];
  /** @type {Textures | null} */
  textures = null;
  /** Size and tier of the render targets. */
  #size = '';

  /** @param {HTMLCanvasElement} canvas */
  constructor(canvas) {
    this.canvas = canvas;
  }

  /** Creates the context and compiles the programs, without blocking the main thread when possible. */
  async init() {
    const gl = this.canvas.getContext('webgl2', {
      antialias: false,
      alpha: false,
      depth: false,
      stencil: false,
      powerPreference: 'high-performance',
      preserveDrawingBuffer: false,
    });
    if (!gl) throw new CabinStop('WebGL2 indisponible');
    this.gl = gl;

    const parallel = gl.getExtension('KHR_parallel_shader_compile');
    const sources = { scene: SCENE_FS, blur: BLUR_FS, comp: COMP_FS };
    /** @type {[string, WebGLProgram, WebGLShader[]][]} */
    const pending = Object.entries(sources).map(([name, fs]) => {
      const shaders = [this.#shader(gl.VERTEX_SHADER, VS), this.#shader(gl.FRAGMENT_SHADER, fs)];
      const program = /** @type {WebGLProgram} */ (gl.createProgram());
      shaders.forEach((shader) => gl.attachShader(program, shader));
      gl.linkProgram(program);
      return [name, program, shaders];
    });

    if (parallel) {
      while (!pending.every(([, program]) => gl.getProgramParameter(program, parallel.COMPLETION_STATUS_KHR))) {
        await wait(8);
        if (!this.gl) return;
      }
    }

    for (const [name, program, shaders] of pending) {
      if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
        const log = shaders.map((shader) => gl.getShaderInfoLog(shader)).join(' ') || gl.getProgramInfoLog(program);
        throw new CabinStop(`programme ${name} : ${log}`);
      }
      shaders.forEach((shader) => gl.deleteShader(shader));
      this.programs[name] = program;
    }

    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
  }

  /**
   * Loads the front and back photos (the same URL once) into textures, without using them yet.
   * @param {string} front
   * @param {string} back
   * @returns {Promise<Textures | null>} null if the renderer was released meanwhile
   */
  async loadTextures(front, back) {
    const frontLoad = loadImage(front);
    const backLoad = back === front ? frontLoad : loadImage(back);
    const [frontImage, backImage] = await Promise.all([frontLoad, backLoad]);
    if (!this.gl) return null;
    const frontTexture = this.#texture(frontImage);
    if (backImage === frontImage) return { front: frontTexture, back: frontTexture };
    await wait(0); // one upload (and its mipmaps) per task
    if (!this.gl) return null;
    return { front: frontTexture, back: this.#texture(backImage) };
  }

  /** @param {Textures} textures replaces the textures in use */
  useTextures(textures) {
    if (textures === this.textures) return;
    this.freeTextures(this.textures);
    this.textures = textures;
  }

  /** @param {Textures | null} textures */
  freeTextures(textures) {
    const gl = this.gl;
    if (!gl || !textures) return;
    gl.deleteTexture(textures.front);
    if (textures.back !== textures.front) gl.deleteTexture(textures.back);
    if (textures === this.textures) this.textures = null;
  }

  /**
   * Sizes the canvas and the render targets for a tier.
   * @param {number} width CSS pixels
   * @param {number} height CSS pixels
   * @param {typeof TIERS[number]} tier
   */
  resize(width, height, tier) {
    const gl = this.gl;
    if (!gl) return false;
    const dpr = Math.min(window.devicePixelRatio || 1, tier.dpr);
    // The canvas is at the screen's density (the image at rest); a gesture's scene, at the tier's.
    const full = Math.min(window.devicePixelRatio || 1, REFINE_DPR);
    const cw = Math.max(2, Math.round(width * full));
    const ch = Math.max(2, Math.round(height * full));
    const size = `${cw}x${ch}@${tier.name}`;
    // Resizing clears the canvas: only when something changed.
    if (size === this.#size) return false;
    this.#size = size;
    this.canvas.width = cw;
    this.canvas.height = ch;
    this.#freeTargets();
    const a = MAX_QUALITY
      ? this.#target(cw, ch)
      : this.#target(Math.round(width * dpr * tier.scale), Math.round(height * dpr * tier.scale));
    const b = this.#target(a.w >> 1, a.h >> 1);
    const c = this.#target(b.w, b.h);
    const f = this.#target(cw, ch);
    this.#targets = [a, b, c, f];
    this.#sharp = a;
    this.#composable = false;
    return true;
  }

  /** The render targets hold an image that the composition alone can reuse. */
  #composable = false;
  /** Whether that image went through the blur. */
  #soft = false;
  /** The scene the composition reads: a gesture's (reduced density), or the one at rest (full density). */
  #sharp = null;

  /**
   * One image: scene → mask-weighted blur (half resolution) → composition.
   * @param {Frame} frame
   * @param {Look} look
   * @param {typeof TIERS[number]} tier
   * @param {number} [sample] -1: a gesture's image. 0, 1, 2…: the image at rest, full density, one more ray
   *   per pixel each time (shifted inside the pixel), averaged with the previous ones.
   */
  draw(frame, look, tier, sample = -1) {
    const gl = this.gl;
    const [a, b, c, f] = this.#targets;
    if (!gl || !a || !this.textures) return;
    const { shape } = frame;
    const t = sample >= 0 ? f : a;

    gl.bindFramebuffer(gl.FRAMEBUFFER, t.fb);
    gl.viewport(0, 0, t.w, t.h);
    const p = this.programs.scene;
    gl.useProgram(p);
    const f1 = (name, v) => gl.uniform1f(this.#loc(p, name), v);
    const f3 = (name, v) => gl.uniform3fv(this.#loc(p, name), v);
    gl.uniform3fv(this.#loc(p, 'uCam'), frame.eye);
    gl.uniformMatrix4fv(this.#loc(p, 'uInvVP'), false, frame.invViewProjection);
    gl.uniform1i(this.#loc(p, 'uNR'), shape.rings.length);
    gl.uniform1i(this.#loc(p, 'uNW'), shape.walls.length);
    gl.uniform1i(this.#loc(p, 'uNP'), frame.postCount);
    gl.uniform1fv(this.#loc(p, 'uRA'), frame.ringApothem);
    gl.uniform1fv(this.#loc(p, 'uRN'), frame.ringPanels);
    gl.uniform1fv(this.#loc(p, 'uRPh'), frame.ringPhase);
    gl.uniform1fv(this.#loc(p, 'uRG'), frame.ringRise);
    gl.uniform4fv(this.#loc(p, 'uROp'), frame.ringOpenings);
    gl.uniform2fv(this.#loc(p, 'uWA'), frame.wallStart);
    gl.uniform2fv(this.#loc(p, 'uWB'), frame.wallEnd);
    gl.uniform1fv(this.#loc(p, 'uWH'), frame.wallHeight);
    gl.uniform3fv(this.#loc(p, 'uPost'), frame.posts);
    const colors = sceneColors();
    f1('uWallH', HOME.wallHeight);
    f1('uHAvg', frame.riseAverage);
    f1('uMaxB', Math.min(look.depth, tier.bounces));
    f3('uZenith', colors.zenith);
    f3('uHorizon', colors.horizon);
    f3('uFloor', colors.floor);
    f3('uWallTint', look.tint);
    f3('uCapTint', colors.cap);
    f3('uLift', colors.lift);
    f3('uGain', colors.gain);
    f1('uSkyPow', HOME.skyPower);
    f1('uFloorSpec', HOME.floorSpecular);
    f1('uLightI', HOME.lightIntensity);
    f1('uSeam', HOME.seam);
    f1('uWave', look.wave);
    f1('uCapR', HOME.capRadius);
    f1('uRailR', HOME.railRadius);
    f1('uPostR', HOME.postRadius);
    f1('uExposure', HOME.exposure);
    f1('uContrast', HOME.contrast);
    f1('uSaturation', HOME.saturation);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.textures.front);
    gl.uniform1i(this.#loc(p, 'uFront'), 0);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, this.textures.back);
    gl.uniform1i(this.#loc(p, 'uBack'), 1);
    gl.uniform2f(this.#loc(p, 'uCard'), frame.card[0], frame.card[1]);
    f1('uProdX', frame.productX);
    f1('uProdY', frame.productY);
    f1('uPhys', shape.physical ? 1 : 0);
    f1('uPx', (2 * Math.tan((frame.fov * DEG) / 2)) / t.h);
    // Ray n ≥ 1 at rest: shifted inside its pixel (R2 sequence), blended in at weight 1 / (n + 1).
    const jx = sample > 0 ? ((0.5 + sample * 0.7548776662) % 1) - 0.5 : 0;
    const jy = sample > 0 ? ((0.5 + sample * 0.569840291) % 1) - 0.5 : 0;
    gl.uniform2f(this.#loc(p, 'uJit'), (2 * jx) / t.w, (2 * jy) / t.h);
    if (sample > 0) {
      gl.enable(gl.BLEND);
      gl.blendColor(0, 0, 0, 1 / (sample + 1));
      gl.blendFunc(gl.CONSTANT_ALPHA, gl.ONE_MINUS_CONSTANT_ALPHA);
    }
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    if (sample > 0) gl.disable(gl.BLEND);

    const soft = tier.blur && look.blur > 0.001;
    if (soft) {
      const blur = this.programs.blur;
      gl.useProgram(blur);
      const pass = (src, dst, dx, dy) => {
        gl.bindFramebuffer(gl.FRAMEBUFFER, dst.fb);
        gl.viewport(0, 0, dst.w, dst.h);
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, src.tex);
        gl.uniform1i(this.#loc(blur, 'uSrc'), 0);
        gl.uniform2f(this.#loc(blur, 'uDir'), dx / dst.w, dy / dst.h);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
      };
      const r = 1 + 2.6 * look.blur;
      pass(t, b, r, 0);
      pass(b, c, 0, r);
      pass(c, b, r * 1.7, 0);
      pass(b, c, 0, r * 1.7);
    }
    this.#soft = soft;
    this.#sharp = t;
    this.#composable = true;
    this.compose(frame, look);
  }

  /**
   * The composition pass alone, over the last scene: enough when only the fade to the background moved.
   * @param {Frame} frame
   * @param {Look} look
   * @returns {boolean} false when a full image is needed
   */
  compose(frame, look) {
    const gl = this.gl;
    const [, , c] = this.#targets;
    const a = this.#sharp;
    if (!gl || !a || !this.#composable) return false;
    const soft = this.#soft;
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, this.canvas.width, this.canvas.height);
    const comp = this.programs.comp;
    gl.useProgram(comp);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, a.tex);
    gl.uniform1i(this.#loc(comp, 'uSharp'), 0);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, c.tex);
    gl.uniform1i(this.#loc(comp, 'uSoft'), 1);
    gl.uniform1f(this.#loc(comp, 'uUseSoft'), soft ? 1 : 0);
    gl.uniform1f(this.#loc(comp, 'uBlur'), look.blur);
    gl.uniform1f(this.#loc(comp, 'uGrain'), look.grain);
    gl.uniform1f(this.#loc(comp, 'uGrainR'), look.grainReflections);
    gl.uniform1f(this.#loc(comp, 'uVignette'), HOME.vignette);
    gl.uniform1f(this.#loc(comp, 'uAspect'), frame.aspect);
    gl.uniform1f(this.#loc(comp, 'uRoom'), frame.room);
    gl.uniform3fv(this.#loc(comp, 'uBgS'), look.background);
    // The image at rest has its own rays per pixel; a gesture's, FXAA.
    const [a0] = this.#targets;
    gl.uniform1f(this.#loc(comp, 'uFxaa'), a === a0 ? 1 : 0);
    gl.uniform2f(this.#loc(comp, 'uTexel'), 1 / a.w, 1 / a.h);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    return true;
  }

  /**
   * Draws one image and waits for the GPU (reading a pixel) to time it.
   * @param {Frame} frame
   * @param {Look} look
   * @param {typeof TIERS[number]} tier
   * @returns {number} milliseconds
   */
  bench(frame, look, tier) {
    const gl = /** @type {WebGL2RenderingContext} */ (this.gl);
    const pixel = new Uint8Array(4);
    const start = performance.now();
    this.draw(frame, look, tier);
    gl.readPixels(0, 0, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, pixel);
    return performance.now() - start;
  }

  /** Releases the GPU resources and the context. */
  destroy() {
    const gl = this.gl;
    if (!gl) return;
    this.#freeTargets();
    this.freeTextures(this.textures);
    Object.values(this.programs).forEach((program) => gl.deleteProgram(program));
    this.programs = {};
    try {
      gl.getExtension('WEBGL_lose_context')?.loseContext();
    } catch {
      // The context may already be lost.
    }
    this.gl = null;
  }

  /** @param {number} type @param {string} source */
  #shader(type, source) {
    const gl = /** @type {WebGL2RenderingContext} */ (this.gl);
    const shader = /** @type {WebGLShader} */ (gl.createShader(type));
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    return shader;
  }

  /** @param {WebGLProgram} program @param {string} name */
  #loc(program, name) {
    const gl = /** @type {WebGL2RenderingContext} */ (this.gl);
    let locations = this.#locations.get(program);
    if (!locations) this.#locations.set(program, (locations = new Map()));
    let location = locations.get(name);
    if (location === undefined) {
      location = gl.getUniformLocation(program, name);
      locations.set(name, location);
    }
    return location;
  }

  /** @param {HTMLImageElement} image */
  #texture(image) {
    const gl = /** @type {WebGL2RenderingContext} */ (this.gl);
    const texture = /** @type {WebGLTexture} */ (gl.createTexture());
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.SRGB8_ALPHA8, gl.RGBA, gl.UNSIGNED_BYTE, image);
    gl.generateMipmap(gl.TEXTURE_2D);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    const anisotropic = gl.getExtension('EXT_texture_filter_anisotropic');
    if (anisotropic) {
      const max = gl.getParameter(anisotropic.MAX_TEXTURE_MAX_ANISOTROPY_EXT);
      gl.texParameterf(gl.TEXTURE_2D, anisotropic.TEXTURE_MAX_ANISOTROPY_EXT, Math.min(8, max));
    }
    return texture;
  }

  /** @param {number} w @param {number} h */
  #target(w, h) {
    const gl = /** @type {WebGL2RenderingContext} */ (this.gl);
    w = Math.max(2, w);
    h = Math.max(2, h);
    const tex = /** @type {WebGLTexture} */ (gl.createTexture());
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    const fb = /** @type {WebGLFramebuffer} */ (gl.createFramebuffer());
    gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
    return { tex, fb, w, h };
  }

  #freeTargets() {
    const gl = this.gl;
    if (!gl) return;
    this.#targets.forEach(({ tex, fb }) => {
      gl.deleteTexture(tex);
      gl.deleteFramebuffer(fb);
    });
    this.#targets = [];
    this.#sharp = null;
  }
}

/** @type {Record<'zenith' | 'horizon' | 'floor' | 'cap' | 'lift' | 'gain', number[]> | null} */
let sceneColorCache = null;

/** Constant scene colours, converted once: linear for lighting, sRGB for grading. */
function sceneColors() {
  return (sceneColorCache ??= {
    zenith: scaled(linear(HOME.zenith), HOME.skyIntensity),
    horizon: scaled(linear(HOME.horizon), HOME.skyIntensity),
    floor: linear(HOME.floorColor),
    cap: linear(HOME.capTint),
    lift: srgb(HOME.lift),
    gain: srgb(HOME.gain),
  });
}

/**
 * Loads a photo for WebGL. The Shopify CDN answers with `Access-Control-Allow-Origin: *`.
 * @param {string} url
 * @returns {Promise<HTMLImageElement>}
 */
async function loadImage(url) {
  const image = new Image();
  image.crossOrigin = 'anonymous';
  image.decoding = 'async';
  image.src = url;
  try {
    await image.decode();
  } catch {
    throw new CabinStop('photo illisible', { remember: false });
  }
  return image;
}

/* ---------------------------------------------------------------------------------------------------
 * Frame state: everything the scene pass needs, updated in place.
 * ------------------------------------------------------------------------------------------------- */

/**
 * @typedef {{ front: WebGLTexture, back: WebGLTexture }} Textures
 * @typedef {ReturnType<typeof createFrame>} Frame
 * @typedef {{ depth: number, blur: number, grain: number, grainReflections: number, wave: number,
 *   tint: number[], background: number[] }} Look
 */

/** @param {typeof SHAPES[keyof typeof SHAPES]} shape */
function createFrame(shape) {
  const ringOpenings = new Float32Array(MAXR * 4);
  shape.rings.forEach((ring, i) =>
    ring.open.forEach(([centre, half], k) => {
      ringOpenings[i * 4 + k * 2] = centre * DEG;
      ringOpenings[i * 4 + k * 2 + 1] = half * DEG;
    }),
  );
  const wallStart = new Float32Array(MAXW * 2);
  const wallEnd = new Float32Array(MAXW * 2);
  shape.walls.forEach(([a, b], i) => {
    wallStart.set(a, i * 2);
    wallEnd.set(b, i * 2);
  });
  /** @param {number[]} values @param {number} size @param {number} filler */
  const fill = (values, size, filler) => Float32Array.from({ length: size }, (_, i) => values[i] ?? filler);

  return {
    shape,
    camera: CAMERAS[shape.camera],
    fov: CAMERAS[shape.camera].fov,
    /** Canvas size in CSS pixels. */
    cssWidth: 0,
    cssHeight: 0,
    aspect: 1,
    distance: 3,
    yaw: 0,
    pitch: 0,
    eye: new Float32Array(3),
    invViewProjection: new Float32Array(16),
    /** Size of the virtual product (the whole photo), in metres. */
    card: [REFERENCE_HEIGHT, REFERENCE_HEIGHT],
    productX: 0,
    productY: PRODUCT_Y,
    ringApothem: fill(
      shape.rings.map((r) => r.a),
      MAXR,
      1,
    ),
    ringPanels: fill(
      shape.rings.map((r) => r.n),
      MAXR,
      8,
    ),
    ringPhase: fill(
      shape.rings.map((r) => (r.phase * 2 * Math.PI) / r.n),
      MAXR,
      0,
    ),
    ringOpenings,
    ringRise: new Float32Array(MAXR),
    wallStart,
    wallEnd,
    wallHeight: new Float32Array(MAXW),
    wallAngle: shape.walls.map(([a, b]) => Math.atan2((a[0] + b[0]) / 2, (a[1] + b[1]) / 2)),
    posts: new Float32Array(MAXP * 3),
    postCount: 0,
    riseAverage: 0,
    room: 0,
  };
}

/** @param {number} angle @param {number} rise 0–1 */
function waveHeight(angle, rise) {
  // Same formula as facetH() in the shader: panels at the back rise first.
  const x = clamp01(rise * 1.35 - (angleDistance(angle, Math.PI) / Math.PI) * 0.35);
  return HOME.wallHeight * x * x * (3 - 2 * x);
}

/**
 * Places the camera around the product centre (yaw and pitch from the pointer) and builds the inverse
 * view-projection used by the scene pass.
 * @param {Frame} frame
 */
function updateCamera(frame) {
  const { camera } = frame;
  const centre = [0, PRODUCT_Y, 0];
  const yaw = frame.yaw;
  const pitch = Math.atan2(camera.height, frame.distance) + frame.pitch;
  const r = Math.hypot(frame.distance, camera.height);
  const eye = [
    centre[0] + Math.sin(yaw) * Math.cos(pitch) * r,
    centre[1] + Math.sin(pitch) * r,
    centre[2] + Math.cos(yaw) * Math.cos(pitch) * r,
  ];
  /** @param {number[]} v */
  const normalize = (v) => {
    const l = Math.hypot(...v);
    return v.map((x) => x / l);
  };
  const z = normalize([eye[0] - centre[0], eye[1] - centre[1], eye[2] - centre[2]]);
  const x = normalize([z[2], 0, -z[0]]);
  const y = [z[1] * x[2] - z[2] * x[1], z[2] * x[0] - z[0] * x[2], z[0] * x[1] - z[1] * x[0]];
  const world = [x[0], x[1], x[2], 0, y[0], y[1], y[2], 0, z[0], z[1], z[2], 0, eye[0], eye[1], eye[2], 1];
  const t = Math.tan((frame.fov * DEG) / 2);
  const a = frame.aspect;
  const near = 0.05;
  const far = 100;
  // Inverse of the perspective projection (column-major).
  // prettier-ignore
  const inverseProjection = [
    t * a, 0, 0, 0,
    0, t, 0, 0,
    0, 0, 0, (near - far) / (2 * far * near),
    0, 0, -1, (far + near) / (2 * far * near),
  ];
  for (let i = 0; i < 4; i++) {
    for (let j = 0; j < 4; j++) {
      let sum = 0;
      for (let k = 0; k < 4; k++) sum += world[k * 4 + j] * inverseProjection[i * 4 + k];
      frame.invViewProjection[i * 4 + j] = sum;
    }
  }
  frame.eye.set(eye);
}

/**
 * Panels, posts and fade for an appearance time and a presence (1 on the first photo, 0 on the next).
 * @param {Frame} frame
 * @param {{ appear: number, presence: number, rise: boolean, fadeIn: boolean, still: boolean }} state
 *   `appear`: time since the appearance started, normalised to APPEAR_REFERENCE (Infinity: done).
 *   `rise`: panels rise from the floor. `fadeIn`: the room fades in over the whole appearance.
 *   `still`: reduced motion, panels never move and leaving is a plain fade.
 * @returns {{ scene: boolean, room: boolean }} what changed: the scene itself, or only the fade
 */
function updatePanels(frame, { appear, presence, rise, fadeIn, still }) {
  const { shape } = frame;
  let changed = false;
  /** @param {Float32Array} array @param {number} i @param {number} value */
  const set = (array, i, value) => {
    if (Math.abs(array[i] - value) > 1e-4) {
      array[i] = value;
      changed = true;
    }
  };

  const roomIn = fadeIn ? smooth(0, APPEAR_REFERENCE, appear) : smooth(0, 0.4, appear);
  const room = Math.min(roomIn, smooth(0, 0.3, presence));
  const roomChanged = Math.abs(frame.room - room) > 1e-4;
  if (roomChanged) frame.room = room;

  // Rings rise one after the other from the centre, and go down from the centre too.
  let sum = 0;
  shape.rings.forEach((_, i) => {
    const up = rise ? easeOut(clamp01((appear - 0.1 - 0.22 * i) / 1.1)) : 1;
    const n = shape.rings.length - 1 - i;
    const g = still ? 1 : Math.min(up, smooth(0.08 + 0.12 * n, 0.62 + 0.12 * n, presence));
    set(frame.ringRise, i, g);
    sum += g;
  });
  const wallUp = rise ? easeOut(clamp01((appear - 0.1) / 0.95)) : 1;
  const wallRise = still ? 1 : Math.min(wallUp, smooth(0.1, 0.7, presence));
  shape.walls.forEach((_, i) => {
    const h = shape.wallWave ? waveHeight(frame.wallAngle[i], wallRise) : HOME.wallHeight * wallRise;
    set(frame.wallHeight, i, h);
    sum += wallRise;
  });
  const count = shape.rings.length + shape.walls.length;
  frame.riseAverage = count ? sum / count : 0;

  // Chrome posts: at the edges of the passages and at the wall corners, as tall as the panels beside them.
  let j = 0;
  /** @param {number} x @param {number} z @param {number} h */
  const post = (x, z, h) => {
    set(frame.posts, j * 3, x);
    set(frame.posts, j * 3 + 1, z);
    set(frame.posts, j * 3 + 2, h);
    j++;
  };
  shape.rings.forEach((ring, i) => {
    const step = (2 * Math.PI) / ring.n;
    const radius = ring.a / Math.cos(step / 2);
    ring.open.forEach(([centre, half]) =>
      [-1, 1].forEach((side) => {
        const angle = (centre + side * half) * DEG;
        const k = Math.round((angle - ring.phase * step) / step + side * 0.5);
        const height = waveHeight(ring.phase * step + k * step, frame.ringRise[i]);
        post(Math.sin(angle) * radius, Math.cos(angle) * radius, height);
      }),
    );
  });
  shape.corners.forEach(({ point, walls }) =>
    post(point[0], point[1], Math.max(...walls.map((w) => frame.wallHeight[w]))),
  );
  if (frame.postCount !== j) {
    frame.postCount = j;
    changed = true;
  }
  return { scene: changed, room: roomChanged };
}

/**
 * @param {string} value computed object-position
 * @param {number} freeX free space along x (negative with cover)
 * @param {number} freeY free space along y
 * @returns {[number, number]} offset of the drawn picture in the image box
 */
function objectPosition(value, freeX, freeY) {
  // Computed values: "50% 50%", "10px 0px", or calc() with a percentage and a length.
  const parts = (value || '').match(/calc\([^)]*\)|\S+/g) ?? [];
  /** @param {string | undefined} part @param {number} free */
  const resolve = (part = '', free) => {
    let offset = 0;
    for (const [, sign, number, unit] of part.matchAll(/([+-]?)\s*(\d*\.?\d+)(%|px)/g)) {
      const amount = (sign === '-' ? -1 : 1) * Number(number);
      offset += unit === '%' ? (amount / 100) * free : amount;
    }
    return /\d/.test(part) && Number.isFinite(offset) ? offset : free / 2;
  };
  return [resolve(parts[0], freeX), resolve(parts[1], freeY)];
}

/** @param {{ front: string, back: string } | null} a @param {{ front: string, back: string } | null} b */
const sameImages = (a, b) => a?.front === b?.front && a?.back === b?.back;

/* ---------------------------------------------------------------------------------------------------
 * The element
 * ------------------------------------------------------------------------------------------------- */

/**
 * @typedef {{ front: string, back: string }} Images
 * @typedef {{ mode: 'desktop' | 'mobile', container: HTMLElement, image: HTMLImageElement,
 *   scroller: HTMLElement | null, shape: keyof typeof SHAPES, host: HTMLElement, stage?: boolean,
 *   ownBox?: boolean, gap?: number, padding?: number, rtl?: boolean }} Media
 *   `container`: the first media. `host`: where the canvas goes, which carries `data-mirror-cabin`: the
 *   container itself, or on mobile the stage behind the slideshow (`stage`), which stays in place while the
 *   photos slide over it. `ownBox`: the canvas has its own place (stage, SDM sheet where a view has no box)
 *   and is what gets measured; otherwise the container is. `gap`, `padding`, `rtl`: layout of the mobile
 *   slideshow, for the progress.
 */

/** Media already carrying a cabin: two blocks in one section never stack two canvases. */
const claimed = new WeakSet();

class MirrorCabin extends HTMLElement {
  static observedAttributes = ['data-front', 'data-back'];

  /** @type {AbortController | null} Listeners living as long as the element. */
  #life = null;
  /** @type {AbortController | null} Listeners and observers of the current media. */
  #hook = null;
  /** @type {AbortController | null} The start in progress (its context-loss listener). */
  #startControl = null;
  /** @type {MutationObserver | null} */
  #galleryObserver = null;

  #config = { ...DEFAULTS };
  /** @type {Record<string, { front?: string | null, back?: string | null }>} */
  #variants = {};
  /** @type {Images | null} Photos of the selected variant, back resolved; null: no cabin for it. */
  #images = null;
  /** @type {Images | null} Photos in the textures. */
  #loaded = null;
  /** @type {Images | null} Photos being loaded. */
  #loading = null;
  /** Latest texture load: an older one that ends later is dropped. */
  #loadGeneration = 0;

  /** @type {Element | null} */
  #section = null;
  /** @type {HTMLElement | null} */
  #gallery = null;
  /** @type {Media | null} */
  #media = null;
  /** @type {HTMLCanvasElement | null} */
  #canvas = null;
  /** @type {Renderer | null} */
  #renderer = null;
  /** @type {Frame | null} */
  #frame = null;
  /** @type {Look | null} */
  #look = null;
  /** Measures the scene depends on, to redraw only when they change. */
  #measured = '';
  #tier = 0;

  /** The browser has been idle after `load`, in a visible tab: the cabin may start. */
  #idle = false;
  #starting = false;
  /** The tab went to the background during the start: its timings mean nothing. */
  #hiddenDuringStart = false;
  #ready = false;
  #stopped = false;
  /** Hidden without being stopped: no media to show, or no photo for the variant. */
  #suspended = false;
  /** The canvas shows once it holds an image of its current place. */
  #revealPending = false;
  #reducedMotion = false;
  /** Morph updates the attributes before the JSON children: read them once it is done. */
  #rereadPending = false;

  #visible = true;
  /** The scene must be drawn again (full image). */
  #dirty = true;
  /** Only the fade to the background moved (composition pass). */
  #dirtyComposition = false;
  /** Rays per pixel already in the image at rest; REFINE_SAMPLES: nothing left to sharpen. */
  #samples = REFINE_SAMPLES;
  #raf = 0;
  #lastTick = 0;
  #lastDrew = false;
  #keepAliveUntil = 0;
  #appearStart = -1;
  #slowFrames = 0;
  #pointer = { x: 0, y: 0, active: false };
  /** Tilt of the phone: current (-1 to 1), last change drawn, rest position (degrees). */
  #tilt = { x: 0, y: 0, active: false, drawnX: 0, drawnY: 0, restX: NaN, restY: NaN, time: 0 };

  connectedCallback() {
    this.#init();
  }

  disconnectedCallback() {
    this.#destroy();
  }

  attributeChangedCallback() {
    this.#reread();
  }

  #init() {
    this.#destroy();
    if (this.#stopped) return;
    // Stopped earlier in the session: no new attempt (the theme editor always tries again).
    if (sessionGet() && !isDesignMode()) return;
    if (/** @type {any} */ (navigator).connection?.saveData) return;

    // The section, or the SDM sheet itself: opened as a layer over the home page, it has no section around.
    this.#section = this.closest('.shopify-section, [data-sdm-fiche]') ?? document.body;
    this.#gallery = this.#section.querySelector(GALLERY_SELECTOR);
    if (!this.#gallery) return;

    this.#readData();
    this.#images = this.#resolveImages(this.dataset.front, this.dataset.back);

    this.#life = new AbortController();
    const { signal } = this.#life;
    this.#section.addEventListener('shopify:product:select', this.#onProductSelect, { signal });
    for (const query of [DESKTOP_QUERY, SDM_DESKTOP_QUERY]) {
      matchMedia(query).addEventListener('change', () => this.#update(), { signal });
    }
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    this.#reducedMotion = reducedMotion.matches;
    reducedMotion.addEventListener(
      'change',
      () => {
        this.#reducedMotion = reducedMotion.matches;
        // On the mobile stage, the product follows its photo or keeps its place at rest.
        this.#measure();
        this.#dirty = true;
        this.#poke();
      },
      { signal },
    );
    document.addEventListener('visibilitychange', this.#onVisibilityChange, { signal });
    this.#watchGallery();
    // Morph (combined listings) may change the JSON children alone: read them again.
    const own = new MutationObserver(() => this.#reread());
    own.observe(this, { childList: true, characterData: true, subtree: true });
    signal.addEventListener('abort', () => {
      own.disconnect();
      this.#galleryObserver?.disconnect();
      this.#galleryObserver = null;
    });

    if (isDesignMode()) {
      document.addEventListener('shopify:section:unload', this.#onSectionUnload, { signal });
      document.addEventListener('shopify:section:load', this.#onSectionLoad, { signal });
    }

    if (document.readyState === 'complete') this.#armStart();
    else window.addEventListener('load', () => this.#armStart(), { signal, once: true });
  }

  /**
   * The page first: the cabin may start once the page has loaded, the browser is idle and the tab is
   * visible (timings taken in a background tab are meaningless).
   */
  #armStart() {
    const signal = this.#life?.signal;
    if (!signal || signal.aborted) return;
    this.#idle = false;
    if (document.hidden) {
      document.addEventListener('visibilitychange', () => this.#armStart(), { signal, once: true });
      return;
    }
    const start = () => {
      if (signal.aborted) return;
      if (document.hidden) {
        this.#armStart();
        return;
      }
      this.#idle = true;
      this.#update();
    };
    if ('requestIdleCallback' in window) requestIdleCallback(start, { timeout: IDLE_TIMEOUT_MS });
    else setTimeout(start, 200);
  }

  /** Releases everything, without remembering anything (element removed, section reloaded). */
  #destroy() {
    this.#life?.abort();
    this.#life = null;
    this.#startControl?.abort();
    this.#startControl = null;
    this.#unhook();
    cancelAnimationFrame(this.#raf);
    this.#raf = 0;
    this.#renderer?.destroy();
    this.#renderer = null;
    this.#canvas?.remove();
    this.#canvas = null;
    this.#frame = null;
    this.#look = null;
    this.#measured = '';
    this.#loaded = this.#loading = null;
    this.#idle = this.#starting = this.#ready = this.#suspended = this.#revealPending = false;
    this.#appearStart = -1;
    this.#resetWatch();
  }

  #readData() {
    /** @param {string} selector */
    const json = (selector) => {
      try {
        return JSON.parse(this.querySelector(selector)?.textContent || '{}') ?? {};
      } catch {
        return {};
      }
    };
    const config = json('script[data-config]');
    this.#config = { ...DEFAULTS };
    for (const key of /** @type {(keyof typeof DEFAULTS)[]} */ (Object.keys(DEFAULTS))) {
      if (config[key] != null && config[key] !== '') /** @type {any} */ (this.#config)[key] = config[key];
    }
    this.#variants = json('script[data-variants]');
  }

  /** Data changed (morph with another product): read it once the morph is over, then follow. */
  #reread() {
    if (!this.#life || this.#rereadPending) return;
    this.#rereadPending = true;
    queueMicrotask(() => {
      this.#rereadPending = false;
      if (!this.#life) return;
      this.#readData();
      this.#images = this.#resolveImages(this.dataset.front, this.dataset.back);
      this.#look = null;
      this.#measured = '';
      this.#update();
    });
  }

  /**
   * @param {string | null | undefined} front
   * @param {string | null | undefined} back
   * @returns {Images | null}
   */
  #resolveImages(front, back) {
    if (!front) return null;
    if (!back) {
      if (this.#config.missingBack !== 'front') return null;
      back = front;
    }
    return { front, back };
  }

  /** Horizon replaces the whole `<media-gallery>` on variant change: watch its parent. */
  #watchGallery() {
    this.#galleryObserver?.disconnect();
    const parent = this.#gallery?.parentElement;
    if (!parent) return;
    this.#galleryObserver ??= new MutationObserver(() => {
      if (this.#gallery?.isConnected) return;
      this.#gallery = this.#section?.querySelector(GALLERY_SELECTOR) ?? null;
      this.#watchGallery();
      this.#update();
    });
    this.#galleryObserver.observe(parent, { childList: true });
  }

  /**
   * Brings the cabin in line with the page: starts it, loads the photos of the variant, hooks it to the
   * first media for the current width, or hides it when there is nothing to show.
   */
  #update() {
    if (!this.#life || this.#stopped || !this.#idle) return;
    if (!this.#ready) {
      if (!this.#starting) this.#start();
      return;
    }
    const images = this.#images;
    if (!images || !this.#hookMedia()) {
      this.#suspend();
      return;
    }
    if (!sameImages(images, this.#loaded)) {
      // Keep what is shown until the photos of the variant are ready.
      this.#swapTextures(images);
      return;
    }
    const minimum = this.#minimumTier();
    if (this.#tier < minimum) this.#tier = minimum;
    this.#measure();
    this.#resizeRenderer();
    this.#resume();
  }

  /* --- Start: deferred, measured -------------------------------------------------------------- */

  async #start() {
    // Another cabin of the page may have stopped meanwhile: no new attempt either.
    if (sessionGet() && !isDesignMode()) return;
    if (!this.#life || !this.#images || !this.#hookMedia()) {
      // Nothing to show for now (presentation, width, variant): #update() starts when that changes.
      return;
    }
    this.#starting = true;
    this.#hiddenDuringStart = false;
    const life = this.#life.signal;
    const control = new AbortController();
    this.#startControl = control;
    const { signal } = control;
    life.addEventListener('abort', () => control.abort(), { signal });
    const startedAt = performance.now();
    const images = this.#images;
    const canvas = /** @type {HTMLCanvasElement} */ (this.#canvas);
    const renderer = new Renderer(canvas);
    this.#renderer = renderer;
    canvas.addEventListener(
      'webglcontextlost',
      (event) => {
        event.preventDefault();
        this.#stop('contexte graphique perdu');
      },
      { signal },
    );

    try {
      // Programs and textures, within the loading budget.
      const texture = this.#textureImages(images);
      const loading = renderer.init().then(() => renderer.loadTextures(texture.front, texture.back));
      loading.catch(() => {});
      const textures = await Promise.race([loading, wait(BUDGET.initMs).then(() => false)]);
      if (signal.aborted) return;
      if (textures === false) throw slowLoad();
      if (!textures) throw new CabinRetry();
      renderer.useTextures(textures);
      this.#loaded = images;

      // The gallery may have changed meanwhile (variant, width): follow it before measuring.
      if (!this.#hookMedia()) throw new CabinRetry();
      this.#tier = this.#minimumTier();
      this.#measure();
      this.#resizeRenderer();
      const frame = this.#frame;
      const look = this.#look;
      if (!frame?.cssWidth || !look) throw new CabinRetry();

      // Benchmark (not in the maximum quality trial: the first tier, whatever it costs).
      if (!MAX_QUALITY) {
        // Benchmark: a full image, panels up, timed up to the GPU; one measure per task. The first one
        // also warms the driver up.
        updatePanels(frame, { appear: Infinity, presence: 1, rise: false, fadeIn: false, still: false });
        await wait(0);
        if (signal.aborted) return;
        renderer.bench(frame, look, TIERS[this.#tier]);
        if (performance.now() - startedAt > BUDGET.initMs) throw slowLoad();

        let fits = false;
        for (; this.#tier < TIERS.length; this.#tier++) {
          await wait(0);
          if (signal.aborted) return;
          this.#resizeRenderer();
          if (renderer.bench(frame, look, TIERS[this.#tier]) <= BUDGET.frameMs[this.#tier]) {
            fits = true;
            break;
          }
        }
        if (!fits) throw new CabinStop('appareil trop lent', { timing: true });
      }
    } catch (error) {
      if (signal.aborted) return;
      if (error instanceof CabinRetry || (this.#hiddenDuringStart && error instanceof CabinStop && error.timing)) {
        this.#abandonStart();
        return;
      }
      this.#stop(error instanceof CabinStop ? error.message : 'initialisation impossible', error);
      return;
    } finally {
      if (this.#startControl === control) this.#starting = false;
    }

    this.#ready = true;
    this.#suspended = true;
    this.#dirty = true;
    this.#update();
  }

  /**
   * Gives up the current start without remembering anything: the tab went to the background, or the
   * photo moved or vanished meanwhile. A fresh canvas and context are used next time.
   */
  #abandonStart() {
    this.#startControl?.abort();
    this.#startControl = null;
    this.#starting = false;
    this.#renderer?.destroy();
    this.#renderer = null;
    this.#canvas?.remove();
    this.#canvas = null;
    this.#unhook();
    this.#loaded = this.#loading = null;
    this.#measured = '';
    if (this.#hiddenDuringStart) this.#armStart();
  }

  /** Tier to start from: "Économie" on mobile starts on the lowest one. */
  #minimumTier() {
    if (MAX_QUALITY) return 0;
    return this.#media?.mode === 'mobile' && this.#config.qualityMobile === 'eco' ? TIERS.length - 1 : 0;
  }

  /**
   * The gallery already shows the front at some width: reuse that URL when it is the same file, so the
   * texture comes from the cache. Otherwise the block's URL.
   * @param {Images} images
   * @returns {Images}
   */
  #textureImages(images) {
    const current = this.#findMedia()?.image.currentSrc;
    if (!current) return images;
    try {
      const shown = new URL(current, location.href);
      const wanted = new URL(images.front, location.href);
      const width = Number(shown.searchParams.get('width'));
      if (shown.pathname !== wanted.pathname || !(width >= 800 && width <= 2048)) return images;
      return { front: shown.href, back: images.back === images.front ? shown.href : images.back };
    } catch {
      return images;
    }
  }

  /** @param {Images} images */
  async #swapTextures(images) {
    const renderer = this.#renderer;
    if (!renderer || sameImages(images, this.#loading)) return;
    const generation = ++this.#loadGeneration;
    this.#loading = images;
    /** @type {Textures | null} */
    let textures = null;
    try {
      const texture = this.#textureImages(images);
      textures = await renderer.loadTextures(texture.front, texture.back);
    } catch {
      if (generation !== this.#loadGeneration || this.#renderer !== renderer) return;
      this.#loading = null;
      if (sameImages(images, this.#images)) this.#suspend();
      return;
    }
    if (this.#renderer !== renderer || !textures) return;
    if (generation !== this.#loadGeneration || !sameImages(images, this.#images)) {
      // A newer variant was chosen meanwhile.
      renderer.freeTextures(textures);
      return;
    }
    renderer.useTextures(textures);
    this.#loading = null;
    this.#loaded = images;
    this.#dirty = true;
    this.#update();
  }

  /* --- Hooking to the gallery --------------------------------------------------------------------- */

  /**
   * Finds the first media for the current width and places the canvas behind it.
   * @returns {boolean} whether the cabin has a place to show
   */
  #hookMedia() {
    const target = this.#findMedia();
    if (!target) {
      this.#unhook();
      return false;
    }
    if (target === this.#media && this.#canvas?.parentElement === target.host) {
      this.#setShape(target.shape);
      return true;
    }

    this.#unhook();
    if (claimed.has(target.host)) return false;
    claimed.add(target.host);
    this.#media = target;
    this.#setShape(target.shape);
    this.#measured = '';

    if (!this.#canvas) {
      this.#canvas = document.createElement('canvas');
      this.#canvas.className = 'mirror-cabin__canvas';
      this.#canvas.setAttribute('aria-hidden', 'true');
    }
    // New place (width change, new gallery, morph): hidden until its first image there.
    this.#canvas.classList.remove('is-on');
    if (this.#ready && !this.#suspended) this.#revealPending = true;
    // The stage comes first, so everything in it paints over the canvas.
    if (target.stage) target.host.prepend(this.#canvas);
    else target.host.append(this.#canvas);

    this.#hook = new AbortController();
    const { signal } = this.#hook;
    const { container, host } = target;
    const box = this.#box();

    const resize = new ResizeObserver(this.#onResize);
    resize.observe(box);
    resize.observe(target.image);
    const intersection = new IntersectionObserver(([entry]) => {
      this.#visible = Boolean(entry?.isIntersecting);
      if (this.#visible) this.#requestFrame();
    });
    intersection.observe(box);
    // Horizon's morph (combined listings) drops nodes and attributes it doesn't know: put them back.
    const guard = new MutationObserver(() => {
      const canvas = this.#canvas;
      if (!canvas) return;
      if (canvas.parentElement !== host) this.#update();
      else if (canvas.classList.contains('is-on') && !('mirrorCabin' in host.dataset)) {
        host.dataset.mirrorCabin = '';
      }
    });
    guard.observe(host, { childList: true, attributes: true, attributeFilter: ['data-mirror-cabin'] });
    signal.addEventListener('abort', () => {
      resize.disconnect();
      intersection.disconnect();
      guard.disconnect();
    });
    target.image.addEventListener('load', this.#onResize, { signal });
    // A photo that slides in (the SDM sheet opened as a layer) moves without changing size.
    target.image.addEventListener('transitionend', this.#onResize, { signal });

    // Progress towards the next photo, from passive listeners only: the scroll stays the page's (desktop)
    // and the slideshow's (mobile). The page may scroll in an element (Horizon's .page-wrapper from 990 px):
    // scroll events don't bubble, but any of them can be caught on the way down.
    if (target.mode === 'desktop') {
      document.addEventListener('scroll', this.#poke, { signal, passive: true, capture: true });
      container.addEventListener('pointermove', this.#onPointerMove, { signal, passive: true });
      container.addEventListener('pointerleave', this.#onPointerLeave, { signal, passive: true });
    } else {
      target.scroller?.addEventListener('scroll', this.#poke, { signal, passive: true });
      if (this.#config.gyro) this.#listenTilt(signal, target.scroller ?? container);
      this.#gallery?.addEventListener('slideshow:select', this.#poke, { signal });
    }
    return true;
  }

  /** @returns {Media | null} */
  #findMedia() {
    const gallery = this.#gallery;
    if (!gallery?.isConnected) return null;
    const config = this.#config;
    const desktopShape = config.shapeDesktop in SHAPES ? config.shapeDesktop : DEFAULTS.shapeDesktop;
    const current = this.#media;

    /** @param {Element | null | undefined} container */
    const photo = (container) => {
      if (!(container instanceof HTMLElement) || !container.getClientRects().length) return null;
      if (!container.classList.contains('product-media-container--image')) return null;
      const img = container.querySelector('.product-media img');
      return img instanceof HTMLImageElement ? img : null;
    };
    /** @param {Omit<Media, 'shape'>} media @param {keyof typeof SHAPES} shape @returns {Media} */
    const media = (media, shape) =>
      current?.container === media.container &&
      current.host === media.host &&
      current.image === media.image &&
      current.shape === shape
        ? current
        : { ...media, shape };

    if (!gallery.matches('media-gallery')) return this.#findSdmMedia(gallery, desktopShape);

    if (matchMedia(DESKTOP_QUERY).matches) {
      // Desktop: "Grid" presentation, one column.
      if (gallery.dataset.presentation !== 'grid' || !gallery.classList.contains('media-gallery--one-column'))
        return null;
      const container = /** @type {HTMLElement} */ (gallery.querySelector('.media-gallery__grid > .product-media-container'));
      const image = photo(container);
      if (!image) return null;
      const found = { mode: 'desktop', container, image, scroller: null, host: container };
      return media(/** @type {Omit<Media, 'shape'>} */ (found), /** @type {keyof typeof SHAPES} */ (desktopShape));
    }

    // Mobile: the slideshow, over a stage (its frame) that stays in place while the photos slide.
    if (config.shapeMobile === 'off') return null;
    const scroller = gallery.querySelector('slideshow-container > slideshow-slides[ref="scroller"]');
    const first = scroller?.querySelector(':scope > slideshow-slide');
    if (first && !first.classList.contains('product-media-container')) {
      // Slideshow jumping over several slides: a placeholder stands in for the first one meanwhile.
      return current?.mode === 'mobile' && current.scroller === scroller && current.container.isConnected
        ? current
        : null;
    }
    const image = photo(first);
    if (!image) return null;
    const found = {
      mode: 'mobile',
      container: /** @type {HTMLElement} */ (first),
      image,
      scroller: /** @type {HTMLElement} */ (scroller),
      host: /** @type {HTMLElement} */ (scroller?.parentElement),
      stage: true,
      ownBox: true,
    };
    const shape = /** @type {keyof typeof SHAPES} */ (config.shapeMobile === 'same' ? desktopShape : 'v');
    return media(/** @type {Omit<Media, 'shape'>} */ (found), shape);
  }

  /**
   * SDM product sheet: the views of the product, one per screen. Desktop: rows of a grid, where a view has
   * no box of its own; mobile: a horizontal carousel of slides, over a stage that covers the carousel from
   * the sheet (positioned by the stylesheet, with `:has()`, while it holds the canvas).
   * @param {HTMLElement} gallery `.sdm-fiche-media`
   * @param {string} desktopShape
   * @returns {Media | null}
   */
  #findSdmMedia(gallery, desktopShape) {
    const config = this.#config;
    const current = this.#media;
    const first = gallery.querySelector(':scope > .sdm-fiche-shot');
    const image = first?.querySelector(':scope > img');
    if (!(first instanceof HTMLElement) || !(image instanceof HTMLImageElement) || !image.getClientRects().length) {
      return null;
    }
    const desktop = matchMedia(SDM_DESKTOP_QUERY).matches;
    const sheet = gallery.parentElement;
    if (!desktop && (config.shapeMobile === 'off' || !sheet?.matches('.sdm-fiche') || !CSS.supports('selector(:has(> *))')))
      return null;
    const container = desktop ? gallery : first;
    const host = desktop ? gallery : /** @type {HTMLElement} */ (sheet);
    const shape = /** @type {keyof typeof SHAPES} */ (desktop || config.shapeMobile === 'same' ? desktopShape : 'v');
    if (current?.container === container && current.host === host && current.image === image && current.shape === shape)
      return current;
    return {
      mode: desktop ? 'desktop' : 'mobile',
      container,
      image,
      scroller: desktop ? null : gallery,
      shape,
      host,
      stage: !desktop,
      ownBox: true,
    };
  }

  /** The element the scene covers, measured for the product (and on desktop for the progress). */
  #box() {
    const media = this.#media;
    if (media?.ownBox && this.#canvas) return this.#canvas;
    return /** @type {HTMLElement} */ (media?.container);
  }

  /**
   * Follows the tilt of the phone. Safari on iPhone only gives it after asking, which takes a tap: the
   * first tap on the photos asks, once.
   * @param {AbortSignal} signal
   * @param {HTMLElement} photos
   */
  #listenTilt(signal, photos) {
    const Orientation = /** @type {any} */ (window).DeviceOrientationEvent;
    if (!Orientation) return;
    const listen = () => window.addEventListener('deviceorientation', this.#onTilt, { signal, passive: true });
    if (typeof Orientation.requestPermission !== 'function') {
      listen();
      return;
    }
    const ask = () =>
      Orientation.requestPermission()
        .then((/** @type {string} */ state) => state === 'granted' && listen())
        .catch(() => {});
    // Already allowed on this site: no tap needed. Otherwise the request fails without a tap, and waits for one.
    Orientation.requestPermission()
      .then((/** @type {string} */ state) => (state === 'granted' ? listen() : null))
      .catch(() => photos.addEventListener('click', ask, { signal, once: true }));
  }

  /** @param {DeviceOrientationEvent} event */
  #onTilt = (event) => {
    if (event.beta == null || event.gamma == null) return;
    // Screen axes, whatever the orientation of the screen.
    const angle = screen.orientation?.angle ?? 0;
    const [x, y] =
      angle === 90
        ? [event.beta, -event.gamma]
        : angle === 270
          ? [-event.beta, event.gamma]
          : angle === 180
            ? [-event.gamma, -event.beta]
            : [event.gamma, event.beta];
    const tilt = this.#tilt;
    const now = event.timeStamp / 1000;
    if (Number.isNaN(tilt.restX)) {
      tilt.restX = x;
      tilt.restY = y;
    } else {
      // The rest position slowly follows the way the phone is held.
      const follow = 1 - Math.exp(-Math.max(now - tilt.time, 0) / TILT.recenter);
      tilt.restX += (x - tilt.restX) * follow;
      tilt.restY += (y - tilt.restY) * follow;
    }
    tilt.time = now;
    const dx = x - tilt.restX;
    const dy = y - tilt.restY;
    // Hand tremor: nothing to redraw.
    if (Math.abs(dx - tilt.drawnX) < TILT.deadZone && Math.abs(dy - tilt.drawnY) < TILT.deadZone) return;
    tilt.drawnX = dx;
    tilt.drawnY = dy;
    tilt.x = Math.max(-1, Math.min(1, dx / TILT.range));
    tilt.y = Math.max(-1, Math.min(1, dy / TILT.range));
    tilt.active = true;
    this.#poke();
  };

  #unhook() {
    this.#hook?.abort();
    this.#hook = null;
    const host = this.#media?.host;
    if (host) {
      claimed.delete(host);
      delete host.dataset.mirrorCabin;
    }
    this.#media = null;
    this.#tilt = { x: 0, y: 0, active: false, drawnX: 0, drawnY: 0, restX: NaN, restY: NaN, time: 0 };
  }

  /** @param {keyof typeof SHAPES} name */
  #setShape(name) {
    const shape = SHAPES[name];
    if (this.#frame?.shape === shape) return;
    this.#frame = createFrame(shape);
    this.#measured = '';
    this.#dirty = true;
  }

  /* --- Measure ------------------------------------------------------------------------------------ */

  /**
   * Reads the box the photo is actually drawn in (object-fit and object-position included) and gives the
   * virtual product that size and place, so its reflections fall right behind the photo whatever the
   * margins and format of the gallery. The camera keeps the mock-up's framing.
   */
  #measure() {
    const frame = this.#frame;
    const media = this.#media;
    if (!frame || !media) return;
    const { image } = media;

    const outer = this.#box().getBoundingClientRect();
    const box = image.getBoundingClientRect();
    if (!outer.width || !outer.height || !box.width || !box.height) return;

    const style = getComputedStyle(image);
    const naturalWidth = image.naturalWidth || Number(image.getAttribute('width')) || box.width;
    const naturalHeight = image.naturalHeight || Number(image.getAttribute('height')) || box.height;
    let width = box.width;
    let height = box.height;
    const fit = style.objectFit;
    if (fit === 'contain' || fit === 'cover' || fit === 'scale-down') {
      let scale = (fit === 'cover' ? Math.max : Math.min)(box.width / naturalWidth, box.height / naturalHeight);
      if (fit === 'scale-down') scale = Math.min(scale, 1);
      width = naturalWidth * scale;
      height = naturalHeight * scale;
    } else if (fit === 'none') {
      width = naturalWidth;
      height = naturalHeight;
    }
    const [offsetX, offsetY] = objectPosition(style.objectPosition, box.width - width, box.height - height);

    // The mobile slideshow: gap between slides and start padding, for the progress.
    const scroller = media.scroller;
    if (scroller) {
      const scrollerStyle = getComputedStyle(scroller);
      media.gap = parseFloat(scrollerStyle.columnGap) || 0;
      media.padding = parseFloat(scrollerStyle.paddingInlineStart) || 0;
      media.rtl = scrollerStyle.direction === 'rtl';
    }
    let left = box.left - outer.left + offsetX;
    const top = box.top - outer.top + offsetY;
    // Stage with reduced motion: the product keeps the place of its photo at rest, nothing moves in the
    // mirrors (the scene only fades).
    if (media.stage && this.#reducedMotion) left -= this.#slideOffset().offset;

    this.#look ??= this.#readLook();
    const measured = [outer.width, outer.height, width, height, left, top, frame.shape.camera].join();
    if (measured === this.#measured) return;
    this.#measured = measured;

    const { camera } = frame;
    const tan = Math.tan((camera.fov * DEG) / 2);
    frame.distance = REFERENCE_HEIGHT / camera.share / 2 / tan;
    const metresPerPixel = (2 * Math.hypot(frame.distance, camera.height) * tan) / outer.height;
    frame.cssWidth = outer.width;
    frame.cssHeight = outer.height;
    frame.aspect = outer.width / outer.height;
    frame.card = [width * metresPerPixel, height * metresPerPixel];
    frame.productX = (left + width / 2 - outer.width / 2) * metresPerPixel;
    frame.productY = PRODUCT_Y - (top + height / 2 - outer.height / 2) * metresPerPixel;
    updateCamera(frame);
    this.#dirty = true;
  }

  /** @returns {Look} */
  #readLook() {
    const config = this.#config;
    const tint = parseColor(config.tint) ?? parseColor(DEFAULTS.tint);
    return {
      depth: Math.max(1, Number(config.depth) || DEFAULTS.depth),
      blur: clamp01(Number(config.blur) / 100),
      grain: Math.max(0, Number(config.grain) / 100) || 0,
      grainReflections: Math.max(0, Number(config.grainReflections) / 100) || 0,
      wave: Math.max(0, Number(config.wave)) * HOME.waveStep || 0,
      tint: toLinear(/** @type {number[]} */ (tint).slice(0, 3)),
      background: this.#background(),
    };
  }

  /**
   * Colour the scene fades to. "Section": the background actually painted behind the photo, which
   * follows the colour scheme. "Custom": the chosen colour.
   * @returns {number[]}
   */
  #background() {
    const config = this.#config;
    if (config.backgroundSource === 'custom') {
      const custom = parseColor(config.background);
      if (custom && custom[3] > 0) return custom.slice(0, 3);
    }
    // The nearest background painted behind the photo within the section (the SDM sheet paints its own)…
    /** @param {Element | null} from @param {Element | null} until */
    const painted = (from, until) => {
      for (let node = from; node && node !== until; node = node.parentElement) {
        const color = parseColor(getComputedStyle(node).backgroundColor);
        if (color && color[3] > 0.99) return color.slice(0, 3);
      }
      return null;
    };
    const host = this.#media?.host ?? null;
    const inSection = painted(host, this.#section?.parentElement ?? null);
    if (inSection) return inSection;
    // …else Horizon's, which paints sections with var(--color-background) from a sibling layer.
    const canvas = this.#canvas;
    if (canvas) {
      canvas.style.backgroundColor = 'var(--color-background)';
      const scheme = parseColor(getComputedStyle(canvas).backgroundColor);
      canvas.style.backgroundColor = '';
      if (scheme && scheme[3] > 0.99) return scheme.slice(0, 3);
    }
    const page = painted(this.#section?.parentElement ?? null, null);
    if (page) return page;
    return /** @type {number[]} */ (parseColor(DEFAULTS.background)).slice(0, 3);
  }

  /** Sizes the canvas for the media and the tier. Resizing clears it: draw again, or hide it until then. */
  #resizeRenderer() {
    const frame = this.#frame;
    if (!frame?.cssWidth || !this.#renderer) return;
    if (!this.#renderer.resize(frame.cssWidth, frame.cssHeight, TIERS[this.#tier])) return;
    this.#dirty = true;
    if (!this.#ready || this.#suspended) return;
    if (this.#canDraw()) this.#drawNow();
    else if (this.#canvas?.classList.contains('is-on')) {
      this.#canvas.classList.remove('is-on');
      this.#revealPending = true;
    }
  }

  #onResize = () => {
    if (!this.#frame || !this.#ready || this.#suspended) return;
    this.#measure();
    this.#resizeRenderer();
    this.#requestFrame();
  };

  /* --- Variants ----------------------------------------------------------------------------------- */

  /**
   * Variant chosen: its photos apply once Horizon has swapped the gallery (when the event's promise
   * resolves), so the reflections never run ahead of the photo in front of them.
   * @param {Event & { detail?: any, promise?: Promise<any> }} event
   */
  #onProductSelect = (event) => {
    if (event.target instanceof Element && event.target.closest('product-card')) return;
    const id = event.detail?.variantId ?? event.detail?.detail?.variantId;
    /** @param {unknown} value */
    const apply = (value) => {
      const variant = String(value ?? '')
        .split('/')
        .pop();
      if (variant) this.#selectVariant(variant);
    };
    if (!event.promise) {
      apply(id);
      return;
    }
    event.promise
      .then((result) => apply(id || result?.detail?.resource?.id || result?.variant?.id))
      .catch(() => {
        // Aborted by a newer choice, or failed: the photo in front did not change either.
      });
  };

  /** @param {string} id */
  #selectVariant(id) {
    const entry = this.#variants[id] ?? this.#variants.default;
    if (!entry) return;
    const images = this.#resolveImages(entry.front, entry.back);
    if (sameImages(images, this.#images)) return;
    this.#images = images;
    this.#update();
  }

  /* --- Show, suspend, stop ------------------------------------------------------------------------ */

  #resume() {
    if (this.#suspended) {
      // Shown again (or for the first time): the appearance plays when the photo is seen.
      this.#suspended = false;
      this.#revealPending = true;
      this.#appearStart = -1;
      this.#dirty = true;
      this.#poke();
      return;
    }
    this.#drawNow();
    this.#requestFrame();
  }

  /** Hides the cabin without stopping it. */
  #suspend() {
    if (!this.#ready) {
      this.#unhook();
      return;
    }
    if (this.#suspended) return;
    this.#suspended = true;
    this.#revealPending = false;
    cancelAnimationFrame(this.#raf);
    this.#raf = 0;
    this.#resetWatch();
    this.#canvas?.classList.remove('is-on');
    this.#unhook();
  }

  /**
   * Stops the cabin: canvas faded out, context released, the native gallery alone. Device failures are
   * remembered for the session.
   * @param {string} reason
   * @param {unknown} [error]
   */
  #stop(reason, error) {
    if (this.#stopped) return;
    this.#stopped = true;
    console.info(`${LOG_PREFIX} coupée : ${reason}`, ...(error && !(error instanceof CabinStop) ? [error] : []));
    const remember = !(error instanceof CabinStop) || error.remember;
    if (remember && !isDesignMode()) sessionSet(reason);

    const canvas = this.#canvas;
    const host = this.#media?.host;
    const renderer = this.#renderer;
    this.#startControl?.abort();
    this.#startControl = null;
    this.#life?.abort();
    this.#life = null;
    this.#hook?.abort();
    this.#hook = null;
    cancelAnimationFrame(this.#raf);
    this.#raf = 0;
    this.#ready = this.#starting = false;
    this.#canvas = null;
    this.#renderer = null;
    this.#media = null;

    if (canvas) {
      canvas.style.transition = `opacity ${FADE_MS}ms ease`;
      canvas.classList.remove('is-on');
    }
    setTimeout(() => {
      renderer?.destroy();
      canvas?.remove();
      if (host) {
        claimed.delete(host);
        delete host.dataset.mirrorCabin;
      }
    }, FADE_MS);
  }

  /* --- Animation loop ------------------------------------------------------------------------------ */

  /** Something moved (scroll, pointer, slide): draw, and keep the loop running to follow the gesture. */
  #poke = () => {
    this.#keepAliveUntil = performance.now() + KEEP_ALIVE_MS;
    this.#requestFrame();
  };

  #requestFrame() {
    if (this.#raf || !this.#ready || this.#suspended || document.hidden) return;
    this.#raf = requestAnimationFrame(this.#tick);
  }

  #canDraw() {
    return this.#ready && !this.#suspended && this.#visible && !document.hidden && Boolean(this.#frame);
  }

  /** Slow frames only count in a row, within one animation. */
  #resetWatch() {
    this.#lastTick = 0;
    this.#lastDrew = false;
    this.#slowFrames = 0;
  }

  /** @param {number} now */
  #tick = (now) => {
    this.#raf = 0;
    // Off screen or in a background tab: the loop pauses until the photo is seen again.
    if (!this.#canDraw()) {
      this.#resetWatch();
      return;
    }
    const seconds = now / 1000;
    const dt = this.#lastTick ? seconds - this.#lastTick : 0;
    this.#lastTick = seconds;

    // Watch: frames still late after drawing, several in a row, drop one tier; past the last one, stop.
    if (this.#lastDrew && dt > 0) {
      this.#slowFrames = dt * 1000 > BUDGET.runMs ? this.#slowFrames + 1 : 0;
      if (!MAX_QUALITY && this.#slowFrames >= BUDGET.runFrames) {
        this.#slowFrames = 0;
        this.#degrade();
        if (this.#stopped) return;
      }
    }

    const moving = this.#animate(seconds, dt);
    this.#lastDrew = this.#render();
    // Nothing moved in this frame: one more ray per pixel on the image at rest. Not watched: heavier on
    // purpose, and never during a gesture.
    const refining = !this.#lastDrew && !moving && this.#refine();
    // A gesture's last image: one more frame to start sharpening it.
    const toRefine = this.#lastDrew && this.#samples < REFINE_SAMPLES;
    if (moving || refining || toRefine || now < this.#keepAliveUntil) this.#raf = requestAnimationFrame(this.#tick);
    else this.#resetWatch();
  };

  /** @returns {boolean} whether a ray per pixel was added to the image at rest */
  #refine() {
    const renderer = this.#renderer;
    if (this.#samples >= REFINE_SAMPLES || !renderer || !this.#frame || !this.#look) return false;
    renderer.draw(this.#frame, this.#look, TIERS[this.#tier], this.#samples++);
    return true;
  }

  /**
   * Updates appearance, presence and parallax.
   * @param {number} seconds
   * @param {number} dt
   * @returns {boolean} whether an animation is still running
   */
  #animate(seconds, dt) {
    const frame = /** @type {Frame} */ (this.#frame);
    const config = this.#config;
    const reduced = this.#reducedMotion;

    if (this.#appearStart < 0) this.#appearStart = seconds;
    const speed = APPEAR_REFERENCE / Math.max(Number(config.appearDuration) || APPEAR_REFERENCE, 0.1);
    const appear = reduced || config.appear === 'none' ? Infinity : (seconds - this.#appearStart) * speed;
    const presence = config.hideOnNext ? 1 - smooth(0.04, 0.62, this.#progress()) : 1;
    // Stage: the virtual product follows its photo across the stage, and its reflections with it, as long
    // as the scene shows (not with reduced motion: reflections sliding in the mirrors are a parallax).
    if (this.#media?.stage && presence > 0 && !reduced) this.#measure();
    const changed = updatePanels(frame, {
      appear,
      presence,
      rise: config.appear === 'rise' && !reduced,
      fadeIn: config.appear === 'fade' && !reduced,
      still: reduced,
    });
    if (changed.scene) this.#dirty = true;
    else if (changed.room) this.#dirtyComposition = true;

    // Parallax: a turn of the view around the product, following the mouse (desktop) or the tilt of the
    // phone (mobile).
    const mode = this.#media?.mode;
    const pointer = config.parallax && mode === 'desktop' && !reduced && this.#pointer.active;
    const tilt = config.gyro && mode === 'mobile' && !reduced && this.#tilt.active;
    const targetYaw = pointer ? this.#pointer.x * POINTER.yaw : tilt ? this.#tilt.x * TILT.yaw : 0;
    const targetPitch = pointer ? -this.#pointer.y * POINTER.pitch : tilt ? -this.#tilt.y * TILT.pitch : 0;
    if (targetYaw !== frame.yaw || targetPitch !== frame.pitch) {
      // The tilt streams in continuously: follow it faster than the mouse.
      const ease = 1 - Math.exp(-dt * (tilt ? 6 : 3));
      let yaw = frame.yaw + (targetYaw - frame.yaw) * ease;
      let pitch = frame.pitch + (targetPitch - frame.pitch) * ease;
      // Close enough: land on the target, so the loop can stop.
      if (Math.abs(targetYaw - yaw) < 2e-4 && Math.abs(targetPitch - pitch) < 2e-4) {
        yaw = targetYaw;
        pitch = targetPitch;
      }
      if (yaw !== frame.yaw || pitch !== frame.pitch) {
        frame.yaw = yaw;
        frame.pitch = pitch;
        updateCamera(frame);
        this.#dirty = true;
      }
    }
    const turning = targetYaw !== frame.yaw || targetPitch !== frame.pitch;
    return (Number.isFinite(appear) && appear < APPEAR_END) || turning;
  }

  /** Draws what changed. @returns {boolean} whether an image was drawn */
  #render() {
    const renderer = this.#renderer;
    const frame = this.#frame;
    const look = this.#look;
    if (!renderer || !frame || !look || !(this.#dirty || this.#dirtyComposition)) return false;
    // Only the fade moved (reduced motion, "Fondu"): the composition pass over the last scene.
    if (this.#dirty || !renderer.compose(frame, look)) {
      if (MAX_QUALITY) {
        for (let n = 0; n < GESTURE_SAMPLES; n++) renderer.draw(frame, look, TIERS[this.#tier], n);
        this.#samples = GESTURE_SAMPLES;
      } else {
        renderer.draw(frame, look, TIERS[this.#tier]);
        this.#samples = 0;
      }
    }
    this.#dirty = this.#dirtyComposition = false;
    if (this.#revealPending && this.#canvas && this.#media) {
      // First image in this place: the canvas can show, and the photos let it through.
      this.#revealPending = false;
      this.#media.host.dataset.mirrorCabin = '';
      this.#canvas.classList.add('is-on');
    }
    return true;
  }

  /** Draws right away (after a resize, which clears the canvas). */
  #drawNow() {
    if (!this.#canDraw()) return;
    this.#animate(performance.now() / 1000, 0);
    this.#render();
    // …and the loop sharpens it once at rest.
    this.#requestFrame();
  }

  /** 0 on the first photo, 1 on the next one. */
  #progress() {
    const media = this.#media;
    if (!media) return 0;
    if (media.mode === 'mobile') {
      const { offset, step } = this.#slideOffset();
      return step ? clamp01(Math.abs(offset) / step) : 0;
    }
    const box = this.#box().getBoundingClientRect();
    return box.height ? clamp01(-box.top / box.height) : 0;
  }

  /**
   * Mobile: how far the first slide is from its place at rest, and the step between two slides. Where the
   * first slide actually is (also right while the slideshow reorders its slides to jump over several).
   * @returns {{ offset: number, step: number }} pixels
   */
  #slideOffset() {
    const media = this.#media;
    const scroller = media?.scroller;
    if (!media || !scroller) return { offset: 0, step: 0 };
    const slide = media.container.getBoundingClientRect();
    const frame = scroller.getBoundingClientRect();
    const padding = media.padding ?? 0;
    const offset = media.rtl ? slide.right - (frame.right - padding) : slide.left - (frame.left + padding);
    return { offset, step: slide.width + (media.gap ?? 0) };
  }

  #degrade() {
    if (this.#tier < TIERS.length - 1) {
      this.#tier++;
      this.#resizeRenderer();
      console.info(`${LOG_PREFIX} qualité ${TIERS[this.#tier].name}`);
    } else {
      this.#stop('rendu trop lent');
    }
  }

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    if (event.pointerType !== 'mouse' || !this.#media) return;
    const rect = this.#box().getBoundingClientRect();
    this.#pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.#pointer.y = ((event.clientY - rect.top) / rect.height) * 2 - 1;
    this.#pointer.active = true;
    this.#poke();
  };

  #onPointerLeave = () => {
    this.#pointer.active = false;
    this.#poke();
  };

  #onVisibilityChange = () => {
    this.#resetWatch();
    if (document.hidden) {
      if (this.#starting) this.#hiddenDuringStart = true;
      cancelAnimationFrame(this.#raf);
      this.#raf = 0;
    } else {
      this.#poke();
    }
  };

  /** @param {Event} event */
  #onSectionUnload = (event) => {
    if (event.target instanceof Node && event.target.contains(this)) this.#destroy();
  };

  /** @param {Event} event */
  #onSectionLoad = (event) => {
    if (event.target instanceof Node && event.target.contains(this) && this.isConnected) this.#init();
  };
}

if (!customElements.get('mirror-cabin')) customElements.define('mirror-cabin', MirrorCabin);
